<template>
    <div>
        <v-dialog
            transition="dialog-bottom-transition"
            v-model="dialog"
            persistent
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('pay_slip.init_pay')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.month')" required/>
                                    <w-month-picker
                                        v-model="annex.month"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="annex.name"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('pay_slip.init_mode')" required/>
                                    <w-enum-select
                                        v-model="mode"
                                        :items="modes"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="mode === 'old'">
                                    <w-label :label="$t('pay_slip.old_month')" required/>
                                    <w-enum-select
                                        v-model="oldAnnexId"
                                        :items="annexes"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="eraseOption"
                                        row
                                    >
                                        <template v-slot:label>
                                            {{ $t('pay_slip.if_pay_exist') }}
                                        </template>
                                        <v-radio
                                            :value="false"
                                            :label="$t('common.keep')"
                                            color="teal"
                                        />
                                        <v-radio
                                            :value="true"
                                            :label="$t('common.replace')"
                                            color="red"
                                        />
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <strong>{{ time }}</strong>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn
                        icon="mdi-google-analytics"
                        color="teal"
                        :text="$t('common.to_init')"
                        :loading="loading || loadingGenerate"
                        @press="save"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useValidation from "@/use/Validation.js";
import {usePaySlipAnnexStore} from "@/stores/PaySlipAnnex.js";
import {usePaySlipStore} from "@/stores/PaySlip.js";
import {computed, onMounted, ref, watch} from "vue";
import axios from "axios";

export default {
    setup() {
        const {mxRules} = useValidation();
        const {$i18n, $refs} = useTools();
        const {dialog, loading} = useSharedData();
        const {getBack, translateMonth, transformFilterValues} = useSharedMethod();
        const paySlipAnnex = usePaySlipAnnexStore();
        const paySlipStore = usePaySlipStore();


        const eraseOption = ref(false);
        const data = ref({
            workers: [],
            filter: {
                allRecords: false,
                phase: "ACTIVE",
            },
        });
        const mode = ref("default");
        const modes = [
            {id: "default", name: $i18n.t("pay_slip.default_values")},
            {id: "old", name: $i18n.t("pay_slip.old_values")},
        ];

        const annex = ref({});
        const oldAnnexId = ref(null);
        const annexes = ref([]);

        function findAllAnnexes() {
            loading.value = true;
            axios.get(`/paySlipAnnexes`, {
                params: transformFilterValues({
                    allRecords: true,
                    descending: true,
                    sortBy: 'month',
                })
            }).then(response => {
                annexes.value = response.data.elements;
            }).finally(() => {
                loading.value = false;
            })
        }

        const i = ref(0);
        const time = computed(() => new Date(i.value * 1000).toISOString().substr(11, 8));

        function increment() {
            i.value++;
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post(`/paySlipAnnexes`, annex.value).then(response => {
                    annex.value = response.data;
                    paySlipAnnex.addPaySlipAnnex(annex.value);
                    paySlipAnnex.setAnnexId(annex.value.id);
                    generate();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        const loadingGenerate = ref(false);

        function generate() {
            if ($refs.form.validate()) {
                let timer = null;
                loadingGenerate.value = true;
                timer = setInterval(increment, 1000);
                const ids = data.value.workers.map((w) => w.id);
                const body = {
                    ids: ids,
                    annexId: annex.value.id,
                    oldAnnexId: mode.value === "old" ? oldAnnexId.value : annex.value.id,
                    eraseOption: eraseOption.value,
                    mode: mode.value,
                };
                axios.put(`/payments/generate`, body, {
                    params: transformFilterValues(data.value.filter),
                }).then(() => {
                    paySlipStore.setRefresh();
                    getBack();
                }).finally(() => {
                    loadingGenerate.value = false;
                    clearInterval(timer);
                });
            }
        }

        const month = computed(() => annex.value.month);
        watch(() => month.value, () => {
            annex.value.name = translateMonth(month.value, true);
        });

        onMounted(() => findAllAnnexes());

        return {
            mxRules,
            dialog,
            loading,
            loadingGenerate,
            eraseOption,
            oldAnnexId,
            annexes,
            annex,
            mode,
            modes,
            time,
            save,
            getBack,
        }
    }
};
</script>